/**
 * Device Fingerprinting Service
 * Generates unique identifiers for user devices to support multi-device E2E encryption
 */

export interface DeviceInfo {
  deviceId: string;
  deviceName: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  browser: string;
  os: string;
}

/**
 * Generate a unique device fingerprint per user
 * Combines multiple browser properties for uniqueness
 * Stores device ID per-user to handle multiple users on same device
 *
 * CRITICAL: Device IDs MUST persist across page refreshes to decrypt messages.
 * If a new device ID is generated, ALL historical messages will show encryption errors.
 */
export function generateDeviceFingerprint(userId?: string): string {
  console.log(`🔍 [DeviceFingerprint] generateDeviceFingerprint called with userId: ${userId}`);

  // If userId is not provided, return null - we CANNOT generate device IDs without user context
  // This prevents creating orphaned device IDs before login
  if (!userId) {
    console.error(`❌ [DeviceFingerprint] No userId provided - cannot generate device ID without user context`);
    throw new Error('Cannot generate device fingerprint without userId');
  }

  // Step 1: Check localStorage FIRST - this is our source of truth
  const storageKey = `broodbook_device_id_user_${userId}`;
  console.log(`🔍 [DeviceFingerprint] Checking localStorage key: ${storageKey}`);

  try {
    const storedDeviceId = localStorage.getItem(storageKey);
    if (storedDeviceId && storedDeviceId.startsWith('dev_')) {
      console.log(`✅ [DeviceFingerprint] FOUND existing device ID in localStorage: ${storedDeviceId}`);
      console.log(`✅ [DeviceFingerprint] Device ID has persisted across page loads - messages will decrypt correctly`);
      return storedDeviceId;
    } else if (storedDeviceId) {
      console.warn(`⚠️ [DeviceFingerprint] Found invalid device ID in localStorage: ${storedDeviceId}, will generate new one`);
    } else {
      console.log(`🆕 [DeviceFingerprint] No existing device ID found in localStorage, will generate new one`);
    }
  } catch (e) {
    console.warn(`⚠️ [DeviceFingerprint] localStorage read failed (iOS PWA?):`, e);
  }

  // Step 2: Generate deterministic ID based on browser fingerprint + userId
  const components = [
    navigator.userAgent,
    navigator.language,
    new Date().getTimezoneOffset().toString(),
    window.screen.width.toString(),
    window.screen.height.toString(),
    window.screen.colorDepth.toString(),
  ];

  console.log(`🔍 [DeviceFingerprint] Browser fingerprint components:`, {
    userAgent: navigator.userAgent.substring(0, 50) + '...',
    language: navigator.language,
    timezone: new Date().getTimezoneOffset(),
    screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`
  });

  // Simple hash function - MUST be deterministic (same inputs = same output)
  const hash = components.join('|').split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);

  // Add user ID to hash for multi-user same-device support
  const userHash = userId.split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);

  const combinedHash = Math.abs(hash + userHash);
  const deterministicId = `dev_${combinedHash.toString(36)}`;

  console.log(`🆕 [DeviceFingerprint] Generated NEW deterministic device ID: ${deterministicId}`);
  console.log(`⚠️ [DeviceFingerprint] WARNING: This is a NEW device - historical messages encrypted for other devices will NOT decrypt`);

  // Step 3: Save to localStorage for next time
  try {
    localStorage.setItem(storageKey, deterministicId);
    console.log(`💾 [DeviceFingerprint] Saved device ID to localStorage for future page loads`);

    // Verify it was saved correctly
    const verification = localStorage.getItem(storageKey);
    if (verification === deterministicId) {
      console.log(`✅ [DeviceFingerprint] Verified device ID persisted correctly in localStorage`);
    } else {
      console.error(`❌ [DeviceFingerprint] localStorage verification FAILED! Saved: ${deterministicId}, Read back: ${verification}`);
    }
  } catch (e) {
    console.error(`❌ [DeviceFingerprint] Failed to save device ID to localStorage (iOS PWA?):`, e);
    console.warn(`⚠️ [DeviceFingerprint] Device ID will NOT persist across page refreshes - messages will show encryption errors!`);
  }

  return deterministicId;
}

/**
 * Detect device type
 */
function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent.toLowerCase();

  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }

  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(navigator.userAgent)) {
    return 'mobile';
  }

  return 'desktop';
}

/**
 * Detect browser name
 */
function getBrowserName(): string {
  const ua = navigator.userAgent;
  let browser = 'Unknown';

  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('SamsungBrowser')) browser = 'Samsung Browser';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
  else if (ua.includes('Edge')) browser = 'Edge';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('MSIE') || ua.includes('Trident/')) browser = 'IE';

  return browser;
}

/**
 * Detect OS
 */
function getOS(): string {
  const ua = navigator.userAgent;
  let os = 'Unknown';

  if (ua.includes('Win')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('X11') || ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return os;
}

/**
 * Generate a human-readable device name
 */
function generateDeviceName(): string {
  const browser = getBrowserName();
  const os = getOS();
  const type = getDeviceType();

  // Examples: "Chrome on Windows", "Safari on iPhone", "Firefox on macOS"
  if (type === 'mobile' && os === 'iOS') {
    return `${browser} on iPhone`;
  } else if (type === 'mobile' && os === 'Android') {
    return `${browser} on Android`;
  } else if (type === 'tablet') {
    return `${browser} on Tablet`;
  } else {
    return `${browser} on ${os}`;
  }
}

/**
 * Get complete device information
 */
export function getDeviceInfo(userId?: string): DeviceInfo {
  return {
    deviceId: generateDeviceFingerprint(userId),
    deviceName: generateDeviceName(),
    deviceType: getDeviceType(),
    browser: getBrowserName(),
    os: getOS(),
  };
}

/**
 * Check if device is already registered
 */
export function isDeviceRegistered(): boolean {
  return localStorage.getItem('broodbook_device_registered') === 'true';
}

/**
 * Mark device as registered
 */
export function markDeviceAsRegistered(): void {
  localStorage.setItem('broodbook_device_registered', 'true');
}

/**
 * Clear device registration (for logout)
 */
export function clearDeviceRegistration(): void {
  // Keep the device ID, just clear registration status
  localStorage.removeItem('broodbook_device_registered');
}

/**
 * Migrate from old global device storage to per-user storage
 * This should be called once after login to clean up old device IDs
 *
 * CRITICAL: Old device IDs in the 3-part format (dev_xxx_yyy_zzz) are NOT registered
 * in the database and will cause encryption errors. We need to clear them and regenerate.
 */
export function migrateDeviceStorage(userId: string): void {
  try {
    console.log(`🔄 [Migration] Starting device storage migration for user ${userId}`);

    // Check if we have any old global device IDs to clean up
    const oldGlobalDeviceId = localStorage.getItem('broodbook_device_id');
    if (oldGlobalDeviceId) {
      console.log(`🗑️ [Migration] Removing old global device ID: ${oldGlobalDeviceId}`);
      localStorage.removeItem('broodbook_device_id');
    }

    // Check if we have a per-user device ID in the old 3-part format
    const perUserKey = `broodbook_device_id_user_${userId}`;
    const existingPerUserDeviceId = localStorage.getItem(perUserKey);

    if (existingPerUserDeviceId) {
      // Check if it's the old 3-part format (dev_xxx_yyy_zzz)
      const parts = existingPerUserDeviceId.split('_');
      if (parts.length > 2) {
        console.log(`🗑️ [Migration] CRITICAL: Found OLD 3-part device ID format: ${existingPerUserDeviceId}`);
        console.log(`🗑️ [Migration] This device ID is NOT registered in database - will cause encryption errors`);
        console.log(`🗑️ [Migration] Clearing old device ID to force re-registration with new format`);

        // Clear the old device ID to force regeneration with new format
        localStorage.removeItem(perUserKey);

        // Also clear registration flag to force full re-registration
        localStorage.removeItem('broodbook_device_registered');

        console.log(`✅ [Migration] Old device ID cleared - next page load will generate NEW device ID`);
        console.log(`✅ [Migration] New device ID will be registered in database with public key`);
      } else {
        console.log(`✅ [Migration] Device ID already in new format: ${existingPerUserDeviceId}`);
      }
    } else {
      console.log(`ℹ️ [Migration] No existing per-user device ID found`);
    }

    console.log(`✅ [Migration] Device storage migration complete`);
  } catch (e) {
    console.error('❌ [Migration] Failed to migrate device storage:', e);
  }
}
